import {DateTime} from "luxon";
import {Button, Paper, Alert, TextInput, NumberInput, SimpleGrid, Card, ThemeIcon, List, Text, Title, Group, Stack} from "@mantine/core";
import {WarningCircleIcon, PackageIcon, ImagesIcon, CheckCircleIcon, WarningIcon} from "@phosphor-icons/react";
import {useContext, useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {Link} from "react-router";
import withRouteErrorBoundary from "../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import {Col, Row} from "../../components/Flex/Flex";
import LoaderScreen from "../../components/Loader/LoaderScreen";
import MessageContext from "../../context/MessageContext.tsx";
import Icon from "../../components/Icon/Icon.tsx";
import IconMap from "../../components/Icon/IconMap.ts";
import Premium from "../../utils/Premium.ts";
import {useUser} from "../../utils/api.ts";
import RedeemVoucher from "./RedeemVoucher";
import {usePaySubscribe} from "./usePaySubscribe";


const PREMIUM_PERKS = [
    {
        title: 'Bounty Hunting',
        icon: <Icon icon={IconMap.BountyHunting} size="1.5rem"/>,
        perks: [
            'Unlimited active contracts',
            'Best scans from your current location',
            'Copy-paste import for contract details',
        ],
    },
    {
        title: 'Hyper Routes',
        icon: <Icon icon={IconMap.HyperRoutes} size="1.5rem"/>,
        perks: [
            'Unlimited saved routes',
            'Exclude specific systems from your routes',
        ],
    },
    {
        title: 'Cargo & Fitouts',
        icon: <PackageIcon size={24}/>,
        perks: [
            'Unlimited saved cargo loadouts & fitouts',
            'Copy-paste import for cargo',
        ],
    },
    {
        title: 'Image Hosting',
        icon: <ImagesIcon size={24}/>,
        perks: [
            `Host images up to ${Premium.IMAGE_HOSTING} MB`,
            'Automatic image optimization',
            'JPG, PNG, GIF & WEBP support',
        ],
    },
    {
        title: 'Asteroid Fields',
        icon: <Icon icon={IconMap.AsteroidMining} size="1.5rem"/>,
        perks: [
            'Keep track of your asteroid fields',
            'Optimize prospecting',
        ]
    },
    {
        title: 'More Exclusive Tools',
        icon: <Icon icon={IconMap.Vision} size="1.5rem"/>,
        perks: [
            'Vision Simulator',
            'Interactive Map (Beta)',
        ],
    },
];


const PerkCard = ({title, icon, perks}) => (
    <Card shadow="sm" p="lg" radius="md" withBorder h="100%">
        <Group gap="sm" mb="md" wrap="nowrap">
            <ThemeIcon size={44} radius="md" variant="light" color="forge">
                {icon}
            </ThemeIcon>
            <Text fw={600} fz="lg">{title}</Text>
        </Group>
        <List
            spacing="xs"
            center
            icon={<CheckCircleIcon size={18} weight="fill" color="var(--mantine-color-green-6)"/>}
        >
            {perks.map(perk => <List.Item key={perk}>{perk}</List.Item>)}
        </List>
    </Card>
);


const AlreadySubscribed = ({expiry}) => {
    return (
        <Alert color="green" style={{marginLeft: 'auto', marginRight: 'auto'}}>
            {"You are subscribed. Your subscription will expire " + DateTime.fromSeconds(expiry).toRelative()}
        </Alert>
    )
}

const cardStyle = {
    display: 'flex', flexDirection: 'column', height: '100%', padding: '1.25rem'
};


const SubscribePage = () => {
    const {success, error} = useContext(MessageContext);
    const queryClient = useQueryClient();
    const {user} = useUser();
    const [giftTarget, setGiftTarget] = useState('');
    const [voucherNumber, setVoucherNumber] = useState(1);


    const {verifying, initializeTransaction} = usePaySubscribe({
        onSuccess: () => {
            success('Payment has been verified', 'Thank for your support!');
            queryClient.invalidateQueries({queryKey: ['user']});
        },
        onError: (title, message) => {
            error(title, message);
        }
    })


    const canExtendSelf = !(user.subLevel > 0) || (user.subExpire > DateTime.now().toSeconds() && user.subExpire <= DateTime.now().plus({ days: 30 }).toSeconds());


    const subscribe = (type) => {
        let url;
        if (type === 'gift') {
            if (giftTarget.length === 0) {
                error('Error', 'Insert the handle for the gift recipient');
                return; // return early
            }
            url = `/subscribe/link/gift/${giftTarget}`;
        } else if(type === 'voucher') {
            url = `/subscribe/link/voucher/${voucherNumber}`;
        } else {
            url = `/subscribe/link/${type}`;
        }

        initializeTransaction(url);
    };


    return (
        <div>
            <title>Subscribe to the Forge!</title>
            {verifying && <LoaderScreen message="Verifying transaction" />}
            <div style={{padding: '0 1rem'}}>
                <Stack gap="lg">
                    <h1 style={{fontWeight: 'bold', fontSize: '3rem', margin: 0}}>Subscribe to the Forge!</h1>

                    {user.subLevel > 0 && <AlreadySubscribed expiry={user.subExpire}/>}

                    <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
                        <div>
                            <Title order={2}>Everything you unlock with Premium</Title>
                            <Text c="dimmed" mt={4}>Lift every limit and get access to premium-only tools and features.</Text>
                        </div>
                        <Group gap={6} align="baseline">
                            <Text fw={700} fz={36} lh={1}>15M</Text>
                            <Text c="dimmed">per year</Text>
                        </Group>
                    </Group>

                    <SimpleGrid cols={{base: 1, sm: 2, lg: 3}} spacing="lg">
                        {PREMIUM_PERKS.map(perk => <PerkCard key={perk.title} {...perk}/>)}
                    </SimpleGrid>

                    <Button component={Link} to="/subscribe/pricing" variant="light" size="md" style={{alignSelf: 'center'}}>
                        View full feature comparison
                    </Button>
                </Stack>
            </div>
            <br />

            <Title order={2} style={{textAlign: 'center'}}>Subscribe to Premium</Title>

            <Row style={{padding: '1rem'}} wrap={false} mobileCol={true} align="stretch" justify="center">
                <Col className="col-12 m    d:col-3">
                    <Paper shadow="xs" p="md" withBorder style={cardStyle}>
                        <h3 style={{marginTop: 0}}>Personal</h3>
                        <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                            <p style={{flexGrow: 1}}>Enjoy all the premium features on your own</p>
                            <div style={{width: '100%', display: 'flex', flexDirection: 'column'}}>
                                <Button fullWidth onClick={() => subscribe('self')} disabled={!canExtendSelf}
                                        title={!canExtendSelf ? 'You are already subscribed' : ''}>
                                    Subscribe Now
                                </Button>
                                {canExtendSelf && user.subLevel>0 && <p style={{color: 'var(--mantine-color-blue-5)'}}>Subscription ends within 30 days, you can extend it now </p>}
                            </div>
                        </div>
                    </Paper>
                </Col>
                <Col className="col-12 md:col-3">
                    <Paper shadow="xs" p="md" withBorder style={cardStyle}>
                        <h3 style={{marginTop: 0}}>Gift</h3>
                        <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                            <p style={{flexGrow: 1}}>Share the benefits of Premium with a friend</p>
                            <TextInput style={{marginBottom: '0.5rem'}} value={giftTarget} onChange={e => setGiftTarget(e.target.value)} placeholder="Enter a friend's handle"/>
                            <Button fullWidth onClick={() => subscribe('gift')} disabled={giftTarget.length === 0}>Buy as a gift</Button>
                        </div>
                    </Paper>
                </Col>
                <Col className="col-12 md:col-3">
                    <Paper shadow="xs" p="md" withBorder style={cardStyle}>
                        <h3 style={{marginTop: 0}}>Voucher</h3>
                        <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                            <p style={{flexGrow: 1}}>Get a redeemable code for a subscription. Perfect for giveaways and raffles!</p>
                            <NumberInput
                                style={{marginBottom: '0.5rem', width: '5em'}}
                                value={voucherNumber}
                                onChange={val => setVoucherNumber(typeof val === 'number' ? val : 1)}
                                placeholder="Number of vouchers"
                            />
                            <Button fullWidth onClick={() => subscribe('voucher')}>Buy Vouchers</Button>
                        </div>
                    </Paper>
                </Col>
                <Col className="col-12 md:col-3">
                    <Paper shadow="xs" p="md" withBorder style={cardStyle}>
                        <h3 style={{marginTop: 0}}>Faction</h3>
                        <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                            <p style={{flexGrow: 1}}>Factions can now buy licenses and assign them to their members!</p>
                            <Link to="/faction/licenses"><Button fullWidth>Manage Faction Licenses</Button></Link>
                        </div>
                    </Paper>
                </Col>
            </Row>
            <div style={{display: 'flex', justifyContent: 'center', margin: '0.5rem 0'}}>
                <Alert color="yellow" icon={<WarningCircleIcon size={16}/>}>
                    Payments are processed automatically, please do not modify the amount or the pre-defined message
                </Alert>
            </div>

            <RedeemVoucher />
        </div>
    )
}


export default withRouteErrorBoundary(SubscribePage);
